import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import Button from "../../components/Buttons/Button";
import api from "../../services/api";
import { useBadgeCounts } from "../../contexts/BadgeCountsContext";
// Same rows, fields and status colors as the Push Schedule page.
import "../PushSchedule/PushSchedule.scss";

const TZ = "America/New_York";
// Broadcast push titles (the venue name) are cut at 25 by the server; manual pushes allow 30.
const BROADCAST_TITLE_MAX = 25;

const formatEastern = (iso) =>
  iso
    ? `${new Intl.DateTimeFormat("en-US", {
        timeZone: TZ, weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      }).format(new Date(iso))} ET`
    : "";

// "3h", "2d": how long a broadcast has waited.
const ageText = (iso) => {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  if (mins < 48 * 60) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / 1440)}d`;
};

const STATUS_LABEL = {
  pending: "Waiting",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
  rejected: "Not approved",
};

const PendingRow = ({ b, onChanged }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  const run = async (kind, fn, okText) => {
    setBusy(kind);
    try {
      await fn();
      toast.success(okText);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setBusy(null);
      onChanged();
    }
  };

  const approve = () => {
    if (!window.confirm(`Send this to ${b.venue_name || "the venue"}'s followers now?`)) return;
    run("approve", () => api.post(`/admin/venue-broadcasts/${b._id}/approve`), "Approved and sent");
  };

  const reject = () =>
    run(
      "reject",
      () => api.post(`/admin/venue-broadcasts/${b._id}/reject`, { reason: reason.trim() }),
      "Not approved. The venue can send a new one."
    );

  return (
    <li className="push-schedule__row">
      <div className="push-schedule__row-head">
        <span className="push-schedule__when">{b.venue_name || "Unknown venue"}</span>
        <span className="push-schedule__status is-pending">
          Waiting{b.requested_at ? ` ${ageText(b.requested_at)}` : ""}
        </span>
      </div>
      <div className="push-schedule__fields">
        <div className="push-schedule__sent-title">
          {b.push_title}{" "}
          <span className="push-schedule__count">
            {Array.from(b.push_title || "").length}/{BROADCAST_TITLE_MAX}
          </span>
        </div>
        <div className="push-schedule__sent-msg">{b.message}</div>
        <div className="push-schedule__audience">
          Requested {formatEastern(b.requested_at)}. {Number(b.followers_displayed || 0).toLocaleString()} followers
          shown to the venue. The real count is taken when it sends.
        </div>
        <label className="push-schedule__field">
          <span className="push-schedule__label">Reason if not approved (optional, the venue sees it)</span>
          <textarea rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      <div className="push-schedule__actions">
        <Button
          type="button"
          gradientButton
          textColor="#FFFFFF"
          onClick={approve}
          disabled={!!busy}
          showLoading={busy === "approve"}
        >
          Approve and send
        </Button>
        <Button
          type="button"
          className="btn-outline-light"
          onClick={reject}
          disabled={!!busy}
          showLoading={busy === "reject"}
        >
          Reject
        </Button>
      </div>
    </li>
  );
};

// Only for a send stuck over 10 minutes: marks it failed and lets the venue send again.
const MarkFailed = ({ b, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const markFailed = async () => {
    if (!window.confirm("Mark this as failed? The venue can send a new broadcast. Inbox messages it already wrote stay.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/venue-broadcasts/${b._id}/mark-failed`);
      toast.success("Marked as failed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setBusy(false);
      onChanged();
    }
  };
  return (
    <div className="push-schedule__actions">
      <Button type="button" className="btn-outline-light" onClick={markFailed} disabled={busy} showLoading={busy}>
        Mark as failed
      </Button>
    </div>
  );
};

const historyNote = (b) =>
  [
    b.status === "sent" && b.sent_at && `Sent ${formatEastern(b.sent_at)}`,
    b.status === "sent" && b.pushed_to != null && `pushed to ${Number(b.pushed_to).toLocaleString()} devices`,
    b.status === "sent" && b.followers_real != null && `${Number(b.followers_real).toLocaleString()} inbox messages`,
    b.status !== "sent" && b.requested_at && `Requested ${formatEastern(b.requested_at)}`,
    b.reviewed_at && `${b.status === "rejected" ? "Rejected" : "Approved"} by ${b.reviewed_by_name || "an admin"}, ${formatEastern(b.reviewed_at)}`,
  ]
    .filter(Boolean)
    .join(". ");

const VenueBroadcasts = () => {
  const [pending, setPending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refreshCounts } = useBadgeCounts();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/venue-broadcasts");
      setPending(data.pending || []);
      setRecent(data.recent || []);
    } catch (error) {
      console.error("Error loading venue broadcasts:", error);
      toast.error("Couldn't load venue broadcasts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChanged = () => {
    load();
    if (refreshCounts) refreshCounts();
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <section className="push-schedule">
            <h2 className="page-title m-0">Venue Broadcasts</h2>
            <p className="push-schedule__intro">
              A venue's broadcast to its followers waits here until you approve it. The push title is the venue
              name, cut at {BROADCAST_TITLE_MAX} characters. Rejecting one lets the venue send a new one right away.
            </p>

            <h3 className="push-schedule__section-title">Waiting ({pending.length})</h3>
            {loading ? (
              <p className="push-schedule__empty">Loading...</p>
            ) : pending.length === 0 ? (
              <p className="push-schedule__empty">Nothing waiting.</p>
            ) : (
              <ul className="push-schedule__list">
                {pending.map((b) => (
                  <PendingRow key={b._id} b={b} onChanged={onChanged} />
                ))}
              </ul>
            )}

            <h3 className="push-schedule__section-title">Recent ({recent.length})</h3>
            {!loading && recent.length === 0 ? (
              <p className="push-schedule__empty">No broadcasts yet.</p>
            ) : (
              <ul className="push-schedule__list">
                {recent.map((b) => (
                  <li key={b._id} className="push-schedule__row is-readonly">
                    <div className="push-schedule__row-head">
                      <span className="push-schedule__when">{b.venue_name || "Unknown venue"}</span>
                      <span className={`push-schedule__status is-${b.status === "rejected" || b.stuck ? "failed" : b.status}`}>
                        {b.stuck ? "Stuck, check before resending" : STATUS_LABEL[b.status] || b.status}
                      </span>
                    </div>
                    <div className="push-schedule__sent-title">{b.push_title}</div>
                    <div className="push-schedule__sent-msg">{b.message}</div>
                    <div className="push-schedule__audience">{historyNote(b)}</div>
                    {b.reject_reason && <div className="push-schedule__proposed">Reason: {b.reject_reason}</div>}
                    {b.error && <div className="push-schedule__error">Error: {b.error}</div>}
                    {b.stuck && (
                      <>
                        <div className="push-schedule__error">
                          Still sending after 10 minutes, so the send likely stopped partway. Check the venue's
                          followers got it before marking it failed. Nothing retries on its own.
                        </div>
                        <MarkFailed b={b} onChanged={onChanged} />
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default VenueBroadcasts;
